import React, { useEffect, useMemo, useRef, useState } from "react";

type ThemeMode = "dark" | "light";
type UCTier = "UC1" | "UC2" | "UC3";
type TemplateId =
  | "MXT_001"
  | "MXT_002"
  | "MXT_003"
  | "MXT_004"
  | "MXT_005"
  | "MXT_006"
  | "MXT_007"
  | "MXT_008";

type Primitive = string | number | boolean | null;
type JsonValue = Primitive | JsonObject | JsonValue[];
interface JsonObject {
  [key: string]: JsonValue;
}

interface ToolContext {
  toolId?: string;
  toolName?: string;
  commandSlug?: string;
  category?: string;
  subcategories?: string[];
  iconEmoji?: string;
}

interface OutputSpec {
  template_id?: TemplateId;
  response_mode?: string;
  downloadable_formats?: string[];
  properties?: Record<string, unknown>;
  meta?: string[];
}

interface RenderMap {
  template_id?: TemplateId;
  blocks?: string[];
  entries?: Array<{ block_id: string; order?: number }>;
}

export interface ResultRendererProps {
  data: JsonObject;
  outputSpec: OutputSpec;
  renderMap?: RenderMap;
  registry: unknown;
  theme?: ThemeMode;
  ucTier?: UCTier;
  userPlan?: string;
  responseMode?: string;
  templateId?: TemplateId;
  toolContext?: ToolContext;
  className?: string;
}

type SlotMap = Record<string, string>;

const TEMPLATE_IDS: TemplateId[] = [
  "MXT_001",
  "MXT_002",
  "MXT_003",
  "MXT_004",
  "MXT_005",
  "MXT_006",
  "MXT_007",
  "MXT_008",
];

const TIER_LABELS: Record<UCTier, string> = {
  UC1: "Free",
  UC2: "Pro",
  UC3: "Pro+",
};

const TIER_SLUGS: Record<UCTier, string> = {
  UC1: "free",
  UC2: "pro",
  UC3: "pro_plus",
};

const LOGO_DATA_URI =
  "data:image/png;base64,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";

function isObject(value: JsonValue | undefined): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function getTemplateId(props: ResultRendererProps): TemplateId {
  const candidate = props.templateId || props.renderMap?.template_id || props.outputSpec?.template_id;
  return TEMPLATE_IDS.includes(candidate as TemplateId) ? (candidate as TemplateId) : "MXT_001";
}

function normalizeData(data: JsonObject): JsonObject {
  const nested = data.result;
  if (isObject(nested)) {
    const rest = { ...data };
    delete rest.result;
    return { ...nested, ...rest };
  }
  return data;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function titleize(value: string): string {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function cleanText(value: string): string {
  return value
    .replace(/^#+\s*/gm, "")
    .replace(/\*\*/g, "")
    .replace(/`/g, "")
    .trim();
}

function formatValue(value: JsonValue | undefined): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)));
  if (typeof value === "string") return cleanText(value);
  if (Array.isArray(value)) return value.map((item) => formatValue(item)).filter(Boolean).join(", ");
  return Object.entries(value)
    .map(([key, item]) => {
      if (typeof item === "boolean") return item ? titleize(key) : "";
      return `${titleize(key)}: ${formatValue(item)}`;
    })
    .filter(Boolean)
    .join(", ");
}

function getPath(data: JsonObject, path: string): JsonValue | undefined {
  return path.split(".").reduce<JsonValue | undefined>((current, key) => {
    if (!isObject(current)) return undefined;
    return current[key];
  }, data);
}

function firstValue(data: JsonObject, keys: string[]): JsonValue | undefined {
  for (const key of keys) {
    const value = key.includes(".") ? getPath(data, key) : data[key];
    if (value === null || value === undefined) continue;
    if (typeof value === "string" && !value.trim()) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    return value;
  }
  return undefined;
}

function firstString(data: JsonObject, keys: string[]): string {
  for (const key of keys) {
    const formatted = formatValue(firstValue(data, [key]));
    if (formatted.trim()) return formatted.trim();
  }
  return "";
}

function responseModeTier(value: string): UCTier | null {
  const normalized = value.toLowerCase().replace(/[\s_-]+/g, "");
  if (["uc3", "pro", "pro+", "proplus", "premium", "superior"].includes(normalized)) return "UC3";
  if (["uc2", "long", "standard"].includes(normalized)) return "UC2";
  if (["uc1", "free", "basic", "starter", "short"].includes(normalized)) return "UC1";
  return null;
}

function responseModeLabel(value: string, tier: UCTier): string {
  const normalized = value.toLowerCase().replace(/[\s_-]+/g, "");
  if (["uc1", "free", "basic", "starter", "short"].includes(normalized)) return "Short";
  if (["uc2", "long", "standard"].includes(normalized)) return "Long";
  if (["uc3", "pro", "pro+", "proplus", "premium", "superior"].includes(normalized)) return "Pro";
  return tier === "UC1" ? "Short" : tier === "UC2" ? "Long" : "Pro";
}

function planTier(value: string): UCTier | null {
  const normalized = value.toLowerCase().replace(/[\s_-]+/g, "");
  if (["pro+", "proplus", "premium", "superior", "enterprise"].includes(normalized)) return "UC3";
  if (["pro", "standard"].includes(normalized)) return "UC2";
  if (["free", "basic", "starter"].includes(normalized)) return "UC1";
  return null;
}

function resolveTier(props: ResultRendererProps, data: JsonObject): UCTier {
  if (props.ucTier) return props.ucTier;

  const mode =
    props.responseMode ||
    firstString(data, ["response_mode", "requested_response_mode", "result_mode", "mode"]);
  const modeTier = responseModeTier(mode);
  if (modeTier) return modeTier;

  const membershipTier = planTier(String(props.userPlan || firstString(data, ["plan", "membership"])));
  return membershipTier || "UC2";
}

function scoreText(data: JsonObject): string {
  const raw = firstValue(data, ["confidence_score", "confidence", "score", "rating", "grade", "profile_score"]);
  if (typeof raw === "number") {
    const value = raw <= 1 ? Math.round(raw * 100) : Math.round(raw);
    return String(value);
  }
  return formatValue(raw) || "82";
}

function statusText(data: JsonObject): string {
  return firstString(data, ["risk_level", "risk", "status", "priority", "difficulty", "intent", "classification", "verdict"]) || "Ready";
}

function splitText(value: string): string[] {
  return cleanText(value)
    .split(/\n|•|- /)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function collectItems(data: JsonObject): string[] {
  const candidates = [
    firstValue(data, ["insights", "strategic_insights", "observations", "findings", "highlights"]),
    firstValue(data, ["supporting_points", "key_points", "bullet_points", "reasons", "actions", "action_items"]),
    firstValue(data, ["rationale", "reasoning", "reason", "analysis", "why"]),
    firstValue(data, ["recommendation", "strategic_recommendation", "next_steps", "action_plan"]),
    firstValue(data, ["content", "markdown_content", "summary"]),
  ];

  const items: string[] = [];
  for (const candidate of candidates) {
    if (!candidate) continue;
    if (Array.isArray(candidate)) {
      for (const item of candidate) {
        const text = formatValue(item);
        if (text) items.push(text);
      }
    } else {
      items.push(...splitText(formatValue(candidate)));
    }
  }

  return [...new Set(items)].slice(0, 36);
}

function arrayFromValue(value: JsonValue | undefined): JsonObject[] {
  if (Array.isArray(value)) {
    return value.map((item) => (isObject(item) ? item : { value: item })).filter(isObject);
  }
  if (isObject(value) && Array.isArray(value.rows)) {
    return value.rows.map((item) => (isObject(item) ? item : { value: item })).filter(isObject);
  }
  return [];
}

function rowsFromData(data: JsonObject): JsonObject[] {
  const sources = [
    firstValue(data, ["rows", "table", "data_table", "main_data_table", "ranked_rows"]),
    firstValue(data, ["comparisons", "competitors", "options", "entities", "ranked_items", "results"]),
    firstValue(data, ["action_items", "pipeline_steps", "variants"]),
  ];

  for (const source of sources) {
    const rows = arrayFromValue(source);
    if (rows.length > 0) return rows;
  }

  return Object.entries(data)
    .filter(([, value]) => value !== null && value !== undefined && !Array.isArray(value) && !isObject(value))
    .slice(0, 12)
    .map(([key, value]) => ({ field: titleize(key), value }));
}

function metricsFromData(data: JsonObject, rows: JsonObject[]): JsonObject[] {
  const sources = [
    firstValue(data, ["metrics", "score_dimensions", "dimensions", "analysis.context_metrics", "context_metrics"]),
    firstValue(data, ["attributes", "signals", "scores"]),
  ];
  for (const source of sources) {
    const metrics = arrayFromValue(source);
    if (metrics.length > 0) return metrics;
  }
  return rows.slice(0, 6).map((row) => ({
    label: formatValue(row.label || row.field || row.name || row.title),
    value: formatValue(row.value || row.score || row.rating || row.status),
    description: formatValue(row.description || row.reason || row.summary || row.snippet),
  }));
}

function headersForRows(rows: JsonObject[]): string[] {
  if (rows.length === 0) return ["Field", "Value"];
  return Object.keys(rows[0]).slice(0, 6);
}

function downloadableFormats(tier: UCTier, outputSpec: OutputSpec): string[] {
  const byTier: Record<UCTier, string[]> = {
    UC1: ["json"],
    UC2: ["json", "csv"],
    UC3: ["json", "csv", "pdf"],
  };
  const declared = Array.isArray(outputSpec.downloadable_formats)
    ? outputSpec.downloadable_formats.map((item) => String(item).toLowerCase())
    : [];
  const allowed = byTier[tier];
  const filtered = declared.length ? allowed.filter((format) => declared.includes(format)) : allowed;
  return filtered.length ? filtered : allowed;
}

function downloadButtons(formats: string[]): string {
  const labels: Record<string, string> = {
    json: "📦 Download JSON",
    csv: "📊 Download CSV",
    pdf: "📄 Download PDF",
  };
  return formats
    .map((format) => `<button class="btn btn-${format}" data-download-format="${format}" data-schema-driven="true" type="button" onclick="window.parent.postMessage({type:'mx-download',format:'${format}'},'*')">${labels[format] || `Download ${format.toUpperCase()}`}</button>`)
    .join("");
}

function setSlot(slots: SlotMap, key: string, value: string | number | boolean | undefined | null): void {
  slots[key] = escapeHtml(formatValue(value as JsonValue));
}

function setRaw(slots: SlotMap, key: string, value: string): void {
  slots[key] = value;
}

function setNumbered(slots: SlotMap, base: string, index: number, value: string | number | boolean | undefined | null): void {
  setSlot(slots, `${base}_${index}`, value);
  setSlot(slots, `${base}_${String(index).padStart(2, "0")}`, value);
}

function rowSlots(row: JsonObject, index: number, headers: string[]): SlotMap {
  const slots: SlotMap = {};
  const title = formatValue(row.title || row.name || row.keyword || row.business_name || row.company || row.field || row.label || row.value);
  const description = formatValue(row.description || row.summary || row.snippet || row.reason || row.value || row.body);
  setSlot(slots, "row_position", row.position || row.rank || index + 1);
  setSlot(slots, "row_title", title);
  setSlot(slots, "row_domain", row.domain || row.url || row.source || row.company || "");
  setSlot(slots, "row_snippet", description);
  setSlot(slots, "row_intent", row.intent || row.category || row.status || "");
  setSlot(slots, "row_type", row.type || row.kind || row.priority || "");
  headers.forEach((header, headerIndex) => {
    setNumbered(slots, "table_cell", index * headers.length + headerIndex + 1, formatValue(row[header]));
  });
  return slots;
}

function renderTemplate(template: string, slots: SlotMap): string {
  return template.replace(/\{\{([^}]+)\}\}/g, (_match, rawKey: string) => {
    const key = String(rawKey).trim();
    return slots[key] ?? "";
  });
}

function expandRepeatedRows(template: string, rows: JsonObject[], headers: string[], slots: SlotMap): string {
  return template.replace(/<tr\b[^>]*data-repeat="table_rows"[^>]*>[\s\S]*?<\/tr>/g, (block) => {
    const usableRows = rows.length ? rows.slice(0, 10) : [{}];
    return usableRows.map((row, index) => renderTemplate(block, { ...slots, ...rowSlots(row, index, headers) })).join("");
  });
}

function filterDownloads(html: string, formats: string[]): string {
  const allowed = new Set(formats);
  return html.replace(/<button\b(?=[^>]*(?:data-download-format|data-format)="([^"]+)")[\s\S]*?<\/button>/g, (button, format) => {
    const normalizedFormat = String(format).toLowerCase();
    if (!allowed.has(normalizedFormat)) return "";
    if (button.includes("postMessage({type:'mx-download'")) return button;
    return button.replace(
      /<button\b/,
      `<button onclick="window.parent.postMessage({type:'mx-download',format:'${normalizedFormat}'},'*')"`,
    );
  });
}

function finalizeRenderedHtml(html: string, slots: SlotMap, stripPlaceholderStyling: boolean): string {
  const visibleMode = slots.pill_mode || "";
  // Keep the MarketXtractor logo that ships inside each template; do not override its src.
  let result = html.replace(
    /\bMode:\s*(Basic|Detailed|Advanced|Free|Pro\+?|Superior)\b/g,
    visibleMode ? `Mode: ${visibleMode}` : "Mode",
  );
  if (stripPlaceholderStyling) {
    // The v8 blank templates wrap some slots in "empty state" dashed-border classes
    // (mx-icon-placeholder / mx-placeholder-inline / mx-placeholder) meant to flag an
    // unfilled placeholder while authoring. The client's own final_samples (real, filled-in
    // output) never carry these classes — confirmed by diffing generated output against
    // final_samples/MXT_001/*_sample.html — so strip them once real content is injected.
    result = result
      .replace(/\s*class="mx-icon-placeholder"/g, "")
      .replace(/\s*class="mx-placeholder-inline"/g, "")
      .replace(/\s*class="mx-placeholder"/g, "");
  }
  return result;
}

function triggerDownload(filename: string, content: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function csvEscape(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

function buildCsv(rows: JsonObject[], headers: string[], data: JsonObject): string {
  if (rows.length > 0) {
    return [
      headers.map(csvEscape).join(","),
      ...rows.map((row) => headers.map((header) => csvEscape(formatValue(row[header]))).join(",")),
    ].join("\n");
  }

  return [
    "Field,Value",
    ...Object.entries(data)
      .filter(([, value]) => value !== null && value !== undefined && !Array.isArray(value) && !isObject(value))
      .map(([key, value]) => `${csvEscape(titleize(key))},${csvEscape(formatValue(value))}`),
  ].join("\n");
}

// The client-provided mx-components-v8 contract uses a shared top-level shape and a
// family-specific `sections` vocabulary. All eight families are mapped below.
const V8_MAPPED_TEMPLATE_IDS: TemplateId[] = TEMPLATE_IDS;

function hasV8OutputShape(data: JsonObject, templateId: TemplateId): boolean {
  if (!V8_MAPPED_TEMPLATE_IDS.includes(templateId)) return false;
  return isObject(data.hero) && isObject(data.score) && isObject(data.decision);
}

function objectProperty(parent: JsonObject | undefined, key: string): JsonObject | undefined {
  const value = parent?.[key];
  return isObject(value) ? value : undefined;
}

function scalarProperty(parent: JsonObject | undefined, key: string): string {
  const value = parent?.[key];
  if (isObject(value)) {
    return formatValue(value.display_value ?? value.value ?? value.body ?? value.description ?? value.title ?? value.label);
  }
  return formatValue(value);
}

function blockItems(block: JsonObject | undefined): JsonObject[] {
  return Array.isArray(block?.items) ? block.items.filter(isObject) : [];
}

function tableRowsFromBlock(block: JsonObject | undefined): JsonObject[] {
  const table = objectProperty(block, "table");
  return Array.isArray(table?.rows) ? table.rows.filter(isObject) : [];
}

function itemText(item: JsonObject | undefined): string {
  return formatValue(item?.value ?? item?.body ?? item?.description ?? item?.reason ?? item?.title ?? item?.label);
}

function cleanOrdinal(value: string): string {
  return value.replace(/^\s*(?:\d+|[•✓])\s*[.)-]?\s*/, "").trim();
}

function numberedSegments(value: string): string[] {
  const matches = [...value.matchAll(/(?:^|\s)(\d+)\s+([\s\S]*?)(?=(?:\s+\d+\s+)|$)/g)];
  return matches.map((match) => match[2].trim()).filter(Boolean);
}

function sentenceSegments(value: string): string[] {
  return value.split(/(?<=[.!?])\s+(?=[A-Z0-9])/).map((part) => part.trim()).filter(Boolean);
}

function splitKnownHeading(value: string): { title: string; body: string } {
  const headings = [
    "Core Positioning", "Best Use", "Content Pattern", "Audience Direction", "Profile Strength",
    "Monetization Fit", "Growth Signal", "Brand Position", "Content Engine", "Decision Context",
    "Posting Frequency", "Content Style", "Audience Fit",
  ];
  const heading = headings.find((candidate) => value.startsWith(`${candidate} `));
  return heading
    ? { title: heading, body: value.slice(heading.length).trim() }
    : { title: "Profile Detail", body: value };
}

function setListValues(slots: SlotMap, base: string, items: JsonObject[], limit: number): void {
  items.slice(0, limit).forEach((item, index) => setNumbered(slots, base, index + 1, cleanOrdinal(itemText(item))));
}

function mapV8FamilySlots(templateId: TemplateId, data: JsonObject, slots: SlotMap): void {
  const topic = objectProperty(data, "topic");
  const sections = objectProperty(data, "sections");
  if (!topic || !sections) return;

  if (templateId === "MXT_002") {
    setSlot(slots, "summary_title", scalarProperty(topic, "title"));
    setSlot(slots, "summary_channel", scalarProperty(topic, "channel"));
    setSlot(slots, "summary_audience", scalarProperty(topic, "audience"));
    setSlot(slots, "summary_tone", scalarProperty(topic, "tone"));

    const brief = objectProperty(sections, "quick_spam_trigger_review");
    setListValues(slots, "brief_body", blockItems(brief), 2);

    const strategy = objectProperty(sections, "rewrite_strategy_breakdown") || objectProperty(sections, "pro_rewrite_strategy");
    blockItems(strategy).slice(0, 3).forEach((item, index) => {
      setNumbered(slots, "strategy_title", index + 1, `Strategy ${index + 1}`);
      setNumbered(slots, "strategy_body", index + 1, cleanOrdinal(itemText(item)));
    });

    const rewriteTable = objectProperty(sections, "trigger_rewrite_table") || objectProperty(sections, "full_trigger_analysis");
    tableRowsFromBlock(rewriteTable).slice(0, 4).forEach((row, index) => {
      const cells = Array.isArray(row.cells) ? row.cells : [];
      setNumbered(slots, "rewrite_original", index + 1, formatValue(cells[0]));
      setNumbered(slots, "rewrite_risk", index + 1, formatValue(cells[1]));
      setNumbered(slots, "rewrite_recommendation", index + 1, formatValue(cells[2]));
      setNumbered(slots, "rewrite_reason", index + 1, formatValue(cells[3]));
    });

    blockItems(objectProperty(sections, "rewritten_sequence")).slice(0, 3).forEach((item, index) => {
      setNumbered(slots, "sequence_title", index + 1, `Message ${index + 1}`);
      setNumbered(slots, "sequence_body", index + 1, cleanOrdinal(itemText(item)));
    });
    setListValues(slots, "action_body", blockItems(objectProperty(sections, "next_actions")), 3);
    setSlot(slots, "recommendation_body", scalarProperty(objectProperty(sections, "final_recommendation"), "value"));
    return;
  }

  if (templateId === "MXT_003") {
    setSlot(slots, "comparison_summary_title", scalarProperty(topic, "title"));
    setSlot(slots, "comparison_recommended_option", scalarProperty(topic, "recommended"));
    setSlot(slots, "comparison_confidence", scalarProperty(topic, "confidence"));
    setSlot(slots, "comparison_risk_tolerance", scalarProperty(topic, "risk_tolerance") || scalarProperty(topic, "tolerance"));

    const scenario = scalarProperty(objectProperty(sections, "scenario_inputs"), "value");
    if (scenario) {
      const parts = [
        ["Goal", scenario.match(/Goal\s+([\s\S]*?)(?=\s+Set\s+Hard\s+Constraint|$)/)?.[1]],
        ["Hard Constraint", scenario.match(/Set\s+Hard\s+Constraint\s+([\s\S]*?)(?=\s+Strict(?:\s+Risk\s+Mode|$)|$)/)?.[1]],
        ["Risk Mode", scenario.match(/Risk\s+Mode\s+([\s\S]*?)(?=\s+Safe$|$)/)?.[1]],
      ];
      parts.forEach(([name, description], index) => {
        if (!description) return;
        setNumbered(slots, "input_name", index + 1, name);
        setNumbered(slots, "input_description", index + 1, description);
        setNumbered(slots, "input_status", index + 1, index === 0 ? "Set" : index === 1 ? "Strict" : "Safe");
      });
    }

    const basicResult = scalarProperty(objectProperty(sections, "basic_result"), "value");
    const rankedValue = scalarProperty(objectProperty(sections, "ranked_options"), "value");
    if (basicResult) {
      setNumbered(slots, "ranked_option_name", 1, scalarProperty(topic, "recommended") || "Recommended Option");
      setNumbered(slots, "ranked_option_description", 1, basicResult);
      setNumbered(slots, "ranked_option_status", 1, scalarProperty(topic, "confidence"));
    } else if (rankedValue) {
      const rankedMatches = [...rankedValue.matchAll(/(.+?)\s+((?:Passed|Fails|Best|Strong|Recommended)[\s\S]*?)\s+(\d{1,3})(?=\s+[A-Z]|$)/g)];
      rankedMatches.slice(0, 3).forEach((match, index) => {
        setNumbered(slots, "ranked_option_name", index + 1, match[1].trim());
        setNumbered(slots, "ranked_option_description", index + 1, match[2].trim());
        setNumbered(slots, "ranked_option_status", index + 1, match[3]);
      });
    }

    const comparison = objectProperty(sections, "side_by_side_comparison");
    const comparisonTable = objectProperty(comparison, "table");
    const columns = Array.isArray(comparisonTable?.columns) ? comparisonTable.columns : [];
    setSlot(slots, "comparison_option_a_name", formatValue(columns[1]));
    setSlot(slots, "comparison_option_b_name", formatValue(columns[2]));
    tableRowsFromBlock(comparison).slice(0, 3).forEach((row, index) => {
      const cells = Array.isArray(row.cells) ? row.cells : [];
      setNumbered(slots, "comparison_criteria", index + 1, formatValue(cells[0]));
      setNumbered(slots, "comparison_option_a_text", index + 1, formatValue(cells[1]));
      setNumbered(slots, "comparison_option_a_tag", index + 1, "Recommended");
      setNumbered(slots, "comparison_option_b_text", index + 1, formatValue(cells[2]));
      setNumbered(slots, "comparison_option_b_tag", index + 1, "Alternative");
    });

    const rules = scalarProperty(objectProperty(sections, "rules_applied"), "value")
      .split(/\s*✓\s*/).map((rule) => rule.trim()).filter(Boolean);
    rules.slice(0, 4).forEach((rule, index) => setNumbered(slots, "rule_applied", index + 1, rule));
    setListValues(slots, "warning_body", blockItems(objectProperty(sections, "warnings")), 3);

    const finalRecommendation = scalarProperty(objectProperty(sections, "final_recommendation"), "value");
    setSlot(slots, "final_recommendation_body", finalRecommendation);
    numberedSegments(finalRecommendation).slice(0, 3).forEach((step, index) => {
      const firstSentence = sentenceSegments(step)[0] || step;
      setNumbered(slots, "action_step_title", index + 1, firstSentence.slice(0, 70));
      setNumbered(slots, "action_step_body", index + 1, step);
    });
    return;
  }

  if (templateId === "MXT_004") {
    setSlot(slots, "snapshot_query", scalarProperty(topic, "title"));
    setSlot(slots, "snapshot_source", scalarProperty(topic, "country") || scalarProperty(topic, "source"));
    setSlot(slots, "snapshot_intent", scalarProperty(topic, "intent"));
    setSlot(slots, "snapshot_export_limit", scalarProperty(topic, "limit"));
    setSlot(slots, "snapshot_summary", scalarProperty(objectProperty(sections, "summary_card"), "value"));

    tableRowsFromBlock(objectProperty(sections, "organic_serp_rows")).slice(0, 10).forEach((row, index) => {
      const cells = Array.isArray(row.cells) ? row.cells : [];
      ["position", "title", "domain", "snippet", "intent", "type"].forEach((field, cellIndex) => {
        setNumbered(slots, `result_${field}`, index + 1, formatValue(cells[cellIndex]));
      });
    });
    setListValues(slots, "observed_pattern", blockItems(objectProperty(sections, "observed_patterns")), 3);

    const metrics = blockItems(objectProperty(sections, "intent_distribution"));
    metrics.slice(0, 3).forEach((item, index) => {
      const value = itemText(item);
      const percentage = value.match(/\b\d+(?:\.\d+)?%/)?.[0] || value;
      setNumbered(slots, "pattern_metric_label", index + 1, formatValue(item.label));
      setNumbered(slots, "pattern_metric_value", index + 1, percentage);
      setNumbered(slots, "pattern_metric_description", index + 1, value.replace(percentage, "").trim());
    });
    const opportunities = blockItems(objectProperty(sections, "seo_direction"));
    const recommendations = blockItems(objectProperty(sections, "recommendations"));
    setListValues(slots, "optimization_opportunity", opportunities.length ? opportunities : recommendations, 4);
    return;
  }

  if (templateId === "MXT_005") {
    setSlot(slots, "audit_subject", scalarProperty(topic, "title"));
    setSlot(slots, "audit_context_country", scalarProperty(topic, "country"));
    setSlot(slots, "audit_context_language", scalarProperty(topic, "language"));
    setSlot(slots, "audit_context_intent", scalarProperty(topic, "intent"));

    tableRowsFromBlock(objectProperty(sections, "factor_table")).slice(0, 3).forEach((row, index) => {
      const cells = Array.isArray(row.cells) ? row.cells : [];
      setNumbered(slots, "audit_issue_name", index + 1, formatValue(cells[0]));
      setNumbered(slots, "audit_issue_severity", index + 1, formatValue(cells[1]));
      setNumbered(slots, "audit_issue_evidence", index + 1, formatValue(cells[2]));
    });

    const recommendation = scalarProperty(objectProperty(sections, "recommendation"), "value");
    sentenceSegments(recommendation).slice(0, 4).forEach((action, index) => setNumbered(slots, "audit_action", index + 1, action));
    setSlot(slots, "final_recommendation_body", recommendation || scalarProperty(objectProperty(sections, "final_decision"), "value"));

    const breakdown = scalarProperty(objectProperty(sections, "competitive_breakdown"), "value");
    const pressureMatches = [...breakdown.matchAll(/([A-Za-z][A-Za-z\s-]*?)\s+(\d{1,3})(?=\s+[A-Z]|$)/g)];
    pressureMatches.slice(0, 5).forEach((match, index) => {
      setNumbered(slots, "audit_pressure_label", index + 1, match[1].trim());
      setNumbered(slots, "audit_pressure_value", index + 1, match[2]);
      setNumbered(slots, "audit_pressure_width", index + 1, match[2]);
    });
    const seoActions = blockItems(objectProperty(sections, "seo_action_plan"));
    if (seoActions.length) setListValues(slots, "audit_action", seoActions, 4);
    setListValues(slots, "long_tail_keyword", blockItems(objectProperty(sections, "long_tail_cluster")), 5);
    return;
  }

  if (templateId === "MXT_006") {
    const profileBlock = Object.values(sections).find((value) => isObject(value) && Array.isArray(value.items)) as JsonObject | undefined;
    const profileItems = blockItems(profileBlock);
    const profileName = scalarProperty(profileBlock, "title") || scalarProperty(topic, "title").replace(/^@/, "");
    const profileSummary = itemText(profileItems[0]);
    const profileScore = profileSummary.match(/\b(\d{1,3})\s+★/)?.[1] || "";
    const stars = profileSummary.match(/★{1,5}/)?.[0] || "★★★★★";

    setSlot(slots, "profile_scan_title", scalarProperty(topic, "title"));
    setSlot(slots, "profile_scan_summary", profileSummary);
    setSlot(slots, "profile_name", profileName);
    setSlot(slots, "profile_metadata", [scalarProperty(topic, "platform"), scalarProperty(topic, "niche"), scalarProperty(topic, "type")].filter(Boolean).join(" · "));
    setSlot(slots, "initials", profileName.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase());
    setSlot(slots, "profile_image_url", "");
    setSlot(slots, "profile_score", profileScore);
    setSlot(slots, "profile_score_label", "Profile Strength");
    setSlot(slots, "profile_rating_visual", stars);
    [scalarProperty(topic, "platform"), scalarProperty(topic, "niche"), scalarProperty(topic, "type")]
      .forEach((value, index) => setNumbered(slots, "profile_context", index + 1, value));
    profileItems.slice(1, 5).forEach((item, index) => {
      setNumbered(slots, "attribute_label", index + 1, formatValue(item.label) || `Attribute ${index + 1}`);
      setNumbered(slots, "attribute_value", index + 1, itemText(item));
    });

    const detailsBlock = objectProperty(sections, "quick_profile_summary")
      || objectProperty(sections, "profile_signals")
      || objectProperty(sections, "executive_profile_summary");
    blockItems(detailsBlock).slice(0, 6).forEach((item, index) => {
      const detail = splitKnownHeading(itemText(item));
      setNumbered(slots, "profile_detail_title", index + 1, detail.title);
      setNumbered(slots, "profile_detail_body", index + 1, detail.body);
    });
    setListValues(slots, "lead_signal", blockItems(objectProperty(sections, "insights")), 3);
    setListValues(slots, "outreach_context", blockItems(objectProperty(sections, "recommendations")), 4);
    return;
  }

  if (templateId === "MXT_007") {
    setSlot(slots, "ranked_result_title", scalarProperty(topic, "title"));
    setSlot(slots, "ranked_context_keyword", scalarProperty(topic, "keyword"));
    setSlot(slots, "ranked_context_audience", scalarProperty(topic, "audience"));
    setSlot(slots, "ranked_context_level", scalarProperty(topic, "level"));
    setSlot(slots, "ranked_context_best_angle", scalarProperty(topic, "best_angle"));
    setSlot(slots, "ranked_context_use", scalarProperty(topic, "recommended_use"));
    blockItems(objectProperty(sections, "top_ranked_options")).slice(0, 10).forEach((item, index) => {
      setNumbered(slots, "ranked_option_title", index + 1, formatValue(item.title) || itemText(item));
      setNumbered(slots, "ranked_option_label", index + 1, formatValue(item.label));
      setNumbered(slots, "ranked_option_reason", index + 1, formatValue(item.reason ?? item.description ?? item.value));
    });
    setSlot(slots, "ranked_decision_body", scalarProperty(objectProperty(sections, "decision"), "value"));
    setSlot(slots, "ranked_action_body", scalarProperty(objectProperty(sections, "action"), "value"));
    setSlot(slots, "ranked_recommendation_body",
      scalarProperty(objectProperty(sections, "quick_recommendation"), "value")
      || scalarProperty(objectProperty(sections, "recommendation"), "value")
      || scalarProperty(objectProperty(sections, "advanced_recommendation"), "value"));
    blockItems(objectProperty(sections, "highlight_angles")).slice(0, 3).forEach((item, index) => {
      setNumbered(slots, "highlight_label", index + 1, formatValue(item.label));
      setNumbered(slots, "highlight_body", index + 1, itemText(item));
    });
    return;
  }

  if (templateId === "MXT_008") {
    setSlot(slots, "automation_title", scalarProperty(topic, "title"));
    setSlot(slots, "automation_use_case", scalarProperty(topic, "use_case"));
    setSlot(slots, "automation_stack", scalarProperty(topic, "stack"));
    setSlot(slots, "automation_constraint", scalarProperty(topic, "constraint"));
    setSlot(slots, "automation_confidence", scalarProperty(topic, "confidence"));
    setSlot(slots, "automation_setup", scalarProperty(topic, "setup"));

    const summaryBlock = objectProperty(sections, "risk_assessment_idea_summary")
      || objectProperty(sections, "decision_engine_summary")
      || objectProperty(sections, "risk_assessment_automation_blueprint");
    setSlot(slots, "summary_title", scalarProperty(summaryBlock, "title"));
    setSlot(slots, "summary_body", scalarProperty(summaryBlock, "value"));
    const idea = blockItems(objectProperty(sections, "idea_summary"))[0];
    setSlot(slots, "idea_title", formatValue(idea?.title ?? idea?.label) || "Automation Idea");
    setSlot(slots, "idea_body", itemText(idea));

    blockItems(objectProperty(sections, "key_metrics")).slice(0, 3).forEach((item, index) => {
      setNumbered(slots, "metric_label", index + 1, formatValue(item.label));
      setNumbered(slots, "metric_value", index + 1, formatValue(item.display_value ?? item.value));
      setNumbered(slots, "metric_body", index + 1, formatValue(item.description ?? item.body));
    });
    blockItems(objectProperty(sections, "insight_blocks")).slice(0, 3).forEach((item, index) => {
      setNumbered(slots, "insight_title", index + 1, formatValue(item.title ?? item.label));
      setNumbered(slots, "insight_body", index + 1, formatValue(item.body ?? item.value));
    });
    setSlot(slots, "workflow_explanation", scalarProperty(objectProperty(sections, "workflow_explanation"), "value"));
    blockItems(objectProperty(sections, "full_automation_pipeline")).slice(0, 5).forEach((item, index) => {
      setNumbered(slots, "pipeline_step_title", index + 1, formatValue(item.title ?? item.step));
      setNumbered(slots, "pipeline_step_body", index + 1, formatValue(item.description ?? item.body ?? item.value));
    });
    setListValues(slots, "recommendation", blockItems(objectProperty(sections, "recommendations")), 3);
    setListValues(slots, "risk_opportunity", blockItems(objectProperty(sections, "risks_opportunities")), 3);
  }
}

function buildSlots(props: ResultRendererProps, tier: UCTier): { slots: SlotMap; rows: JsonObject[]; headers: string[]; formats: string[] } {
  const data = normalizeData(props.data || {});
  const rows = rowsFromData(data);
  const headers = headersForRows(rows);
  const metrics = metricsFromData(data, rows);
  const items = collectItems(data);
  const formats = downloadableFormats(tier, props.outputSpec || {});

  const toolTitle = props.toolContext?.toolName || firstString(data, ["tool_title", "tool.name", "tool", "tool_name"]) || "MarketXtractor Result";
  const toolId = props.toolContext?.toolId || firstString(data, ["tool_id", "id"]) || "";
  const subcategory =
    props.toolContext?.subcategories?.[0] ||
    props.toolContext?.category ||
    firstString(data, ["subcategory", "category", "lead_type", "campaign_type"]) ||
    "MarketXtractor";
  const summary =
    firstString(data, ["executive_summary", "main_result", "summary", "headline", "title", "primary_result", "profile.profile_focus"]) ||
    firstString(data, ["intent", "classification", "product", "brand", "topic", "keyword", "query"]) ||
    toolTitle;
  const recommendation =
    firstString(data, ["strategic_recommendation", "recommendation", "next_step", "next_steps", "action_plan", "recommended_page_type"]) ||
    items[0] ||
    summary;
  const rationale = firstString(data, ["rationale", "reasoning", "reason", "analysis", "why"]) || items[1] || recommendation;
  const score = scoreText(data);
  const status = statusText(data);
  const modeLabel = TIER_LABELS[tier];
  const modeSlug = TIER_SLUGS[tier];
  const requestedMode = props.responseMode || firstString(data, ["response_mode", "requested_response_mode", "mode"]) || modeSlug;
  const visibleMode = responseModeLabel(requestedMode, tier);
  const scalarPairs = Object.entries(data)
    .filter(([, value]) => value !== null && value !== undefined && !Array.isArray(value) && !isObject(value))
    .slice(0, 12);

  const slots: SlotMap = {};
  setSlot(slots, "theme", props.theme || "dark");
  setSlot(slots, "result_mode", modeLabel);
  setSlot(slots, "uc_mode", tier);
  setSlot(slots, "uc_mode_slug", modeSlug);
  setSlot(slots, "template_title", `${getTemplateId(props)} ${modeLabel}`);
  setSlot(slots, "document_title_1", toolTitle);
  setSlot(slots, "brand_name", "MarketXtractor");
  setSlot(slots, "tool_icon", props.toolContext?.iconEmoji || firstString(data, ["tool_icon", "icon"]) || "🧩");
  setSlot(slots, "tool_title", toolTitle);
  setSlot(slots, "tool_id", toolId);
  setSlot(slots, "engine_version", firstString(data, ["engine_version", "model", "provider"]) || "MX Engine");
  setRaw(slots, "logo_src", LOGO_DATA_URI);
  setRaw(slots, "mx_logo_src", LOGO_DATA_URI);
  setRaw(slots, "marketxtractor_logo_src", LOGO_DATA_URI);

  setSlot(slots, "pill_subcategory", subcategory);
  setSlot(slots, "pill_confidence", `Confidence ${score}`);
  setSlot(slots, "pill_confidence_status", `Confidence ${score}`);
  setSlot(slots, "pill_risk_status", status);
  setSlot(slots, "pill_risk_level", status);
  setSlot(slots, "pill_status", status);
  setSlot(slots, "pill_tool_id", toolId);
  setSlot(slots, "pill_mode", visibleMode);
  setSlot(slots, "pill_engine_version", firstString(data, ["engine_version", "provider"]) || "Schema-driven");
  setSlot(slots, "pill_lead_or_campaign_type", firstString(data, ["lead_type", "campaign_type", "type"]) || status);
  [subcategory, `Confidence ${score}`, status, toolId, visibleMode, "Schema-driven"].forEach((value, index) => {
    setNumbered(slots, "pill_label", index + 1, value);
  });

  setSlot(slots, "primary_label", "Primary Result");
  setSlot(slots, "primary_result_label", "Primary Result");
  setSlot(slots, "primary_result", summary);
  setSlot(slots, "primary_result_title", summary);
  setSlot(slots, "primary_query", firstString(data, ["keyword", "query", "topic", "product", "brand"]) || summary);
  setSlot(slots, "primary_query_or_dataset", firstString(data, ["keyword", "query", "dataset", "topic", "product", "brand"]) || summary);
  setSlot(slots, "primary_content_title_1", summary);
  setSlot(slots, "primary_content_subtitle_1", rationale);
  setSlot(slots, "selected_option", firstString(data, ["winner", "best_option", "selected_option", "decision", "intent"]) || summary);
  setSlot(slots, "selected_option_label", "Selected Option");
  setSlot(slots, "decision_icon", "✓");
  setSlot(slots, "decision_title", firstString(data, ["decision", "verdict"]) || "Recommendation");
  setSlot(slots, "decision_context_label", "Decision Context");
  setSlot(slots, "decision_context", rationale);

  setSlot(slots, "summary_label", "Summary");
  setSlot(slots, "summary_section_title", "Executive Summary");
  setSlot(slots, "summary_copy", rationale);
  setSlot(slots, "summary_copy_1", rationale);
  setSlot(slots, "body_copy_1", rationale);
  setSlot(slots, "brief_label", "Input");
  setSlot(slots, "brief_section_title", "Result Brief");
  setSlot(slots, "meta_item_1", subcategory);
  setSlot(slots, "meta_item_2", visibleMode);

  setSlot(slots, "score_value", score);
  setSlot(slots, "score_label", firstValue(data, ["confidence", "confidence_score"]) !== undefined ? "Confidence" : "Score");
  setSlot(slots, "confidence_score", score);
  setSlot(slots, "confidence_score_label", "Confidence");
  setSlot(slots, "score_status_label", "Status");
  setSlot(slots, "score_status_value", status);
  setSlot(slots, "risk_level", status);
  setSlot(slots, "warning_text", status);

  setSlot(slots, "recommendation_title", "Recommendation");
  setSlot(slots, "recommendation_title_1", "Recommendation");
  setSlot(slots, "recommendation_text", recommendation);
  setSlot(slots, "recommendation_body", recommendation);
  setSlot(slots, "recommendation_body_1", recommendation);
  setSlot(slots, "final_recommendation_text", recommendation);
  setSlot(slots, "recommendations_section_title", "Recommended Actions");
  setSlot(slots, "action_plan_icon", "↳");
  setSlot(slots, "action_plan_title", "Action Plan");

  setSlot(slots, "analysis_section_title", "Supporting Analysis");
  setSlot(slots, "dimensions_title", "Key Dimensions");
  setSlot(slots, "dimensions_context_label", "Signals");
  for (let index = 1; index <= 7; index += 1) {
    setNumbered(slots, "section_icon", index, ["◈", "◆", "◇", "▣", "▤", "▥", "▧"][index - 1]);
    setNumbered(slots, "section_title", index, ["Summary", "Metrics", "Data", "Analysis", "Recommendations", "Execution", "Downloads"][index - 1]);
    setNumbered(slots, "section_header", index, ["Summary", "Metrics", "Data", "Analysis", "Recommendations", "Execution", "Downloads"][index - 1]);
  }
  setSlot(slots, "section_label", "MarketXtractor Result");
  setSlot(slots, "section_label_01", "MarketXtractor Result");

  for (let index = 1; index <= 6; index += 1) {
    const metric = metrics[index - 1] || {};
    const pair = scalarPairs[index - 1];
    const label = formatValue(metric.label || metric.name || metric.title || metric.field) || (pair ? titleize(pair[0]) : `Metric ${index}`);
    const value = formatValue(metric.value || metric.score || metric.rating || metric.text) || (pair ? formatValue(pair[1]) : index === 1 ? score : "");
    const description = formatValue(metric.description || metric.body || metric.reason || metric.summary) || items[index - 1] || status;
    setNumbered(slots, "metric_label", index, label);
    setNumbered(slots, "metric_value", index, value);
    setNumbered(slots, "metric_description", index, description);
    setSlot(slots, `metric_${String(index).padStart(2, "0")}_label`, label);
    setSlot(slots, `metric_${String(index).padStart(2, "0")}_text`, `${value}${description ? ` - ${description}` : ""}`);
    setNumbered(slots, "dimension", index, label);
    setNumbered(slots, "dimension_name", index, label);
    setNumbered(slots, "dimension_value", index, value);
    setNumbered(slots, "dimension_status", index, description || status);
    setNumbered(slots, "attribute", index, label);
    setSlot(slots, `attribute_${String(index).padStart(2, "0")}_label`, label);
    setSlot(slots, `attribute_${String(index).padStart(2, "0")}_value`, value);
    setNumbered(slots, "bar", index, label);
    setNumbered(slots, "bar_label", index, label);
    setNumbered(slots, "bar_value", index, value);
    setNumbered(slots, "bar_width", index, `${Math.max(20, Math.min(100, Number(value) || Number(score) || 72))}%`);
    setNumbered(slots, "mini", index, label);
    setNumbered(slots, "score_or_status", index, value || status);
  }
  setSlot(slots, "dimension_name", firstString(data, ["dimension", "category"]) || "Primary Signal");
  setSlot(slots, "dimension_value", score);
  setSlot(slots, "dimension_status", status);
  setSlot(slots, "bar_label", "Confidence");
  setSlot(slots, "bar_value", score);

  for (let index = 1; index <= 12; index += 1) {
    const item = items[index - 1] || "";
    setNumbered(slots, "list_item", index, item);
    setNumbered(slots, "bullet_item", index, item);
    setNumbered(slots, "action_item", index, item);
    setNumbered(slots, "checklist_item", index, item);
    setNumbered(slots, "paragraph", index, item);
  }

  for (let index = 1; index <= 6; index += 1) {
    const row = rows[index - 1] || {};
    const name = formatValue(row.name || row.title || row.keyword || row.field || row.label || `Item ${index}`);
    const desc = formatValue(row.description || row.summary || row.snippet || row.reason || row.value || items[index - 1]);
    setNumbered(slots, "item_name", index, name);
    setNumbered(slots, "item_description", index, desc);
    setNumbered(slots, "comparison_text", index, desc || name);
    setNumbered(slots, "comparison_tag", index, formatValue(row.status || row.score || row.type) || "Signal");
    setNumbered(slots, "rank_position", index, row.rank || index);
    setNumbered(slots, "rank_title", index, name);
    setNumbered(slots, "rank_summary", index, desc);
    setNumbered(slots, "rank_status", index, formatValue(row.status || row.score || row.type) || "Ready");
  }

  ["A", "B", "C"].forEach((label, index) => {
    setNumbered(slots, "comparison_header", index + 1, `Option ${label}`);
    setNumbered(slots, "comparison_criteria", index + 1, headers[index] ? titleize(headers[index]) : `Criteria ${index + 1}`);
  });

  headers.slice(0, 6).forEach((header, index) => {
    setNumbered(slots, "table_header", index + 1, titleize(header));
  });
  setSlot(slots, "table_section_title", "Result Data");
  setSlot(slots, "table_header_position", "#");
  setSlot(slots, "table_header_title", headers[0] ? titleize(headers[0]) : "Title");
  setSlot(slots, "table_header_domain", headers[1] ? titleize(headers[1]) : "Source");
  setSlot(slots, "table_header_snippet", headers[2] ? titleize(headers[2]) : "Details");
  setSlot(slots, "table_header_intent", headers[3] ? titleize(headers[3]) : "Signal");
  setSlot(slots, "table_header_type", headers[4] ? titleize(headers[4]) : "Type");
  Object.assign(slots, rowSlots(rows[0] || {}, 0, headers));

  for (let index = 1; index <= 5; index += 1) {
    setNumbered(slots, "context_label", index, scalarPairs[index - 1] ? titleize(scalarPairs[index - 1][0]) : `Context ${index}`);
    setNumbered(slots, "context_value", index, scalarPairs[index - 1] ? formatValue(scalarPairs[index - 1][1]) : items[index - 1] || "");
    setSlot(slots, `context_pill_${String(index).padStart(2, "0")}`, scalarPairs[index - 1] ? `${titleize(scalarPairs[index - 1][0])}: ${formatValue(scalarPairs[index - 1][1])}` : "");
  }
  setSlot(slots, "context_country", firstString(data, ["country", "location", "market"]) || "Global");
  setSlot(slots, "context_language", firstString(data, ["language"]) || "Default");
  setSlot(slots, "context_intent", firstString(data, ["intent", "goal", "objective"]) || status);
  setSlot(slots, "context_country_or_source", firstString(data, ["country", "source", "location"]) || "MarketXtractor");
  setSlot(slots, "context_intent_or_goal", firstString(data, ["intent", "goal", "objective"]) || status);
  setSlot(slots, "context_export_limit", modeLabel);

  setSlot(slots, "profile_avatar", props.toolContext?.iconEmoji || "🧩");
  setSlot(slots, "profile_name", firstString(data, ["profile_name", "business_name", "company", "brand", "name"]) || summary);
  setSlot(slots, "profile_metadata", firstString(data, ["profile_metadata", "category", "location"]) || subcategory);
  setSlot(slots, "profile_score", score);
  setSlot(slots, "profile_score_label", "Profile Score");
  setSlot(slots, "profile_rating_visual", "★★★★★");
  setSlot(slots, "core_offer_title_1", summary);
  setSlot(slots, "core_offer_description_1", rationale);
  setSlot(slots, "highlight_title_1", "Primary Signal");
  setSlot(slots, "highlight_title_2", "Opportunity");
  setSlot(slots, "highlight_title_3", "Next Move");

  for (let index = 1; index <= 5; index += 1) {
    setNumbered(slots, "pipeline_step", index, `Step ${index}`);
    setNumbered(slots, "pipeline_step_1", index, items[index - 1] || "");
    setSlot(slots, `pipeline_step_${index}_label`, `Step ${index}`);
    setSlot(slots, `pipeline_step_${index}_title`, items[index - 1] ? titleize(items[index - 1].slice(0, 42)) : `Execution Step ${index}`);
    setSlot(slots, `pipeline_step_${index}_description`, items[index - 1] || recommendation);
  }

  setSlot(slots, "meta_label_audience", "Audience");
  setSlot(slots, "meta_value_audience", firstString(data, ["audience"]) || "Target users");
  setSlot(slots, "meta_label_channel", "Channel");
  setSlot(slots, "meta_value_channel", firstString(data, ["channel"]) || subcategory);
  setSlot(slots, "meta_label_tone", "Tone");
  setSlot(slots, "meta_value_tone", firstString(data, ["tone"]) || "Professional");
  setSlot(slots, "rewritten_sequence_title", "Generated Sequence");
  for (let index = 1; index <= 4; index += 1) {
    setSlot(slots, `table_row_${index}_original`, rows[index - 1] ? formatValue(rows[index - 1][headers[0]]) : "");
    setSlot(slots, `table_row_${index}_rewrite`, rows[index - 1] ? formatValue(rows[index - 1][headers[1]]) : "");
    setSlot(slots, `table_row_${index}_reason`, rows[index - 1] ? formatValue(rows[index - 1][headers[2]]) : items[index - 1] || "");
    setSlot(slots, `table_row_${index}_risk_level`, status);
    setSlot(slots, `sequence_message_${index}_label`, `Message ${index}`);
    setSlot(slots, `sequence_message_${index}_title`, items[index - 1] ? titleize(items[index - 1].slice(0, 42)) : "");
    setSlot(slots, `sequence_message_${index}_body`, items[index - 1] || "");
    setSlot(slots, `outline_item_${index}_title`, items[index - 1] ? titleize(items[index - 1].slice(0, 42)) : "");
    setSlot(slots, `outline_item_${index}_description`, items[index - 1] || "");
  }

  setRaw(slots, "download_buttons", downloadButtons(formats));
  setRaw(slots, "schema_driven_download_buttons", downloadButtons(formats));
  setSlot(slots, "downloadable_formats_field", formats.join(", "));
  setSlot(slots, "content_1", "");
  setSlot(slots, "content_placeholder_01", "");

  // Shared mx-components-v8 fields. Family-specific section mappings are applied last.
  const isV8MappedTemplate = V8_MAPPED_TEMPLATE_IDS.includes(getTemplateId(props));
  const asScalar = (value: JsonValue | undefined): string | number | boolean | undefined =>
    value === null || value === undefined ? undefined : isObject(value) || Array.isArray(value) ? formatValue(value) : value;

  if (isV8MappedTemplate) {
    const hero = isObject(data.hero) ? data.hero : undefined;
    const topic = isObject(data.topic) ? data.topic : undefined;
    const scoreBlock = isObject(data.score) ? data.score : undefined;
    const decisionBlock = isObject(data.decision) ? data.decision : undefined;
    const sectionsBlock = isObject(data.sections) ? data.sections : undefined;

    if (hero) {
      setSlot(slots, "hero_title", asScalar(hero.title));
      setSlot(slots, "hero_icon", asScalar(hero.icon));
      const pills = Array.isArray(hero.pills) ? hero.pills : [];
      for (let index = 1; index <= 6; index += 1) {
        const pill = pills[index - 1];
        setNumbered(slots, "hero_pill", index, asScalar(isObject(pill) ? pill.value : pill));
      }
    }

    if (topic) {
      setSlot(slots, "decision_title", asScalar(topic.title));
      const recommended = isObject(topic.recommended) ? topic.recommended.value : topic.recommended;
      setSlot(slots, "decision_recommended", asScalar(recommended));
      // UC1's output_spec_json uses `topic.risk_tolerance`, UC2/UC3 use `topic.tolerance`
      // for the same field — accept either key name.
      const toleranceField = topic.risk_tolerance ?? topic.tolerance;
      const tolerance = isObject(toleranceField) ? toleranceField.value : toleranceField;
      setSlot(slots, "decision_tolerance", asScalar(tolerance));
    }

    if (scoreBlock) {
      setSlot(slots, "score_value", asScalar(scoreBlock.display_value ?? scoreBlock.value));
      setSlot(slots, "score_label", asScalar(scoreBlock.label));
      setSlot(slots, "score_progress", asScalar(scoreBlock.value));
      const riskLevel = isObject(scoreBlock.risk_level) ? scoreBlock.risk_level.value : scoreBlock.risk_level;
      if (riskLevel !== undefined) setSlot(slots, "risk_level", asScalar(riskLevel));
    }

    if (decisionBlock && Array.isArray(decisionBlock.items)) {
      decisionBlock.items.forEach((item, index) => {
        if (!isObject(item) || index >= 4) return;
        setNumbered(slots, "dimension_name", index + 1, asScalar(item.label));
        setNumbered(slots, "dimension_value", index + 1, asScalar(item.value));
        setNumbered(slots, "dimension_status", index + 1, asScalar(item.status));
      });
    }

    if (sectionsBlock) {
      const finalDecision = isObject(sectionsBlock.final_decision)
        ? sectionsBlock.final_decision
        : isObject(sectionsBlock.recommendation)
        ? sectionsBlock.recommendation
        : undefined;
      if (finalDecision) setSlot(slots, "final_decision_body", asScalar(finalDecision.value));

      const actionPlan = isObject(sectionsBlock.action_plan) ? sectionsBlock.action_plan : undefined;
      if (actionPlan && Array.isArray(actionPlan.items)) {
        actionPlan.items.forEach((item, index) => {
          if (index >= 4) return;
          setNumbered(slots, "action_body", index + 1, asScalar(isObject(item) ? item.value : item));
        });
      }

      const warnings = isObject(sectionsBlock.warnings) ? sectionsBlock.warnings : undefined;
      if (warnings && Array.isArray(warnings.items)) {
        warnings.items.forEach((item, index) => {
          if (index >= 3) return;
          setNumbered(slots, "warning_body", index + 1, asScalar(isObject(item) ? item.value : item));
        });
      }

      // UC2 "factor_*" cells are a straight positional passthrough of the ranked_options
      // table (name/signal/impact/action <- Option/Score/Constraint/Risk Note), confirmed
      // byte-for-byte against the client's own final_samples/MXT_001/MXT_001_UC2_*_sample.html
      // (the column headers there are static text; only the 4 cell values are placeholders).
      const rankedOptions = isObject(sectionsBlock.ranked_options) ? sectionsBlock.ranked_options : undefined;
      const rankedTable = rankedOptions && isObject(rankedOptions.table) ? rankedOptions.table : undefined;
      const rankedRows = rankedTable && Array.isArray(rankedTable.rows) ? rankedTable.rows : [];
      rankedRows.forEach((row, index) => {
        if (!isObject(row) || index >= 3 || !Array.isArray(row.cells)) return;
        const cells = row.cells;
        setNumbered(slots, "factor_name", index + 1, asScalar(cells[0]));
        setNumbered(slots, "factor_signal", index + 1, asScalar(cells[1]));
        setNumbered(slots, "factor_impact", index + 1, asScalar(cells[2]));
        setNumbered(slots, "factor_action", index + 1, asScalar(cells[3]));
      });

      // UC3 "pressure_*" slots need 4 separate (label, value) bars; risk_pressure_breakdown
      // arrives as one concatenated string with no delimiters ("SMS compliance exposure 76
      // Cold email compliance burden 32 ..."). Splitting on "words followed by a 1-3 digit
      // number" was cross-checked against final_samples/MXT_001/MXT_001_UC3_*_sample.html and
      // matches all 4 bars exactly. If future AI-generated text doesn't follow this shape,
      // slots are simply left blank rather than showing a bad split.
      const pressureBreakdown = isObject(sectionsBlock.risk_pressure_breakdown)
        ? sectionsBlock.risk_pressure_breakdown
        : undefined;
      const pressureText = pressureBreakdown ? asScalar(pressureBreakdown.value) : undefined;
      if (typeof pressureText === "string") {
        const pressureMatches = [...pressureText.matchAll(/([A-Za-z][A-Za-z\s]*?)\s+(\d{1,3})(?=\s|$)/g)].slice(0, 4);
        pressureMatches.forEach((match, index) => {
          const label = match[1].trim();
          const value = match[2];
          setNumbered(slots, "pressure_label", index + 1, label);
          setNumbered(slots, "pressure_value", index + 1, value);
          setNumbered(slots, "pressure_width", index + 1, value);
        });
      }

      mapV8FamilySlots(getTemplateId(props), data, slots);
    }
  }

  return { slots, rows, headers, formats };
}

export default function ResultRenderer(props: ResultRendererProps) {
  const { theme = "dark", className } = props;
  const templateId = getTemplateId(props);
  const normalizedData = useMemo(() => normalizeData(props.data || {}), [props.data]);
  const tier = resolveTier(props, normalizedData);
  const isV8Result = hasV8OutputShape(normalizedData, templateId);
  const v8TemplateUrl = `/templates/${templateId}_${tier}_v8.html`;
  const canonicalTemplateUrl = `/templates/${templateId}_${tier}_${theme}.html`;
  const legacyTemplateUrl = `/templates/${templateId}_${theme}_base.html`;
  const templateUrls = isV8Result
    ? [v8TemplateUrl, canonicalTemplateUrl, legacyTemplateUrl]
    : [canonicalTemplateUrl, legacyTemplateUrl];
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [template, setTemplate] = useState("");
  const [height, setHeight] = useState(900);
  const [error, setError] = useState("");
  const { slots, rows, headers, formats } = useMemo(
    () => buildSlots(props, tier),
    [props.data, props.outputSpec, props.renderMap, props.toolContext, props.templateId, props.ucTier, props.userPlan, props.responseMode, props.theme, tier],
  );

  useEffect(() => {
    let cancelled = false;
    setError("");
    setTemplate("");

    async function loadFirstAvailable() {
      for (const url of templateUrls) {
        try {
          const response = await fetch(url);
          if (response.ok) {
            const html = await response.text();
            if (!cancelled) setTemplate(html);
            return;
          }
        } catch {
          // try next candidate
        }
      }
      if (!cancelled) setError(`Template not found: ${templateUrls[0]}`);
    }

    loadFirstAvailable();

    return () => {
      cancelled = true;
    };
  }, [templateUrls.join("|")]);

  const srcDoc = useMemo(() => {
    if (!template) return "";
    const expanded = expandRepeatedRows(template, rows, headers, slots);
    return finalizeRenderedHtml(filterDownloads(renderTemplate(expanded, slots), formats), slots, isV8Result);
  }, [template, slots, rows, headers, formats, isV8Result]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const message = event.data;
      if (!message || message.type !== "mx-download") return;

      const format = String(message.format || "").toLowerCase();
      const baseName = `${props.toolContext?.toolId || templateId}-${tier}`.toLowerCase();
      if (format === "json") {
        triggerDownload(`${baseName}.json`, JSON.stringify(normalizedData, null, 2), "application/json");
      } else if (format === "csv") {
        triggerDownload(`${baseName}.csv`, buildCsv(rows, headers, normalizedData), "text/csv");
      } else if (format === "pdf") {
        const frame = iframeRef.current;
        frame?.contentWindow?.focus();
        frame?.contentWindow?.print();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [headers, normalizedData, props.toolContext?.toolId, rows, templateId, tier]);

  const resizeIframe = () => {
    const frame = iframeRef.current;
    const doc = frame?.contentDocument;
    if (!doc) return;
    const nextHeight = Math.max(720, doc.documentElement.scrollHeight, doc.body?.scrollHeight || 0);
    setHeight(nextHeight + 24);
    window.setTimeout(() => {
      const lateHeight = Math.max(720, doc.documentElement.scrollHeight, doc.body?.scrollHeight || 0);
      setHeight(lateHeight + 24);
    }, 150);
  };

  if (error) {
    return <div className={className}>Unable to load result template: {error}</div>;
  }

  if (!srcDoc) {
    return <div className={className}>Loading result template...</div>;
  }

  return (
    <div className={className} data-template-id={templateId} data-theme={theme} data-uc-tier={tier}>
      <iframe
        ref={iframeRef}
        title={`${templateId} ${tier} result`}
        srcDoc={srcDoc}
        onLoad={resizeIframe}
        style={{
          width: "100%",
          height,
          border: 0,
          display: "block",
          background: "transparent",
        }}
      />
    </div>
  );
}
